import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatINR, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ProductMediaUploader, mediaSrc } from "@/components/ProductMediaUploader";
import { Package, TrendingUp, Clock, CheckCircle2, Plus, ShieldCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function VendorDashboard() {
  const nav = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    try {
      const v = await http.get("/vendors/me");
      setVendor(v.data);
      const p = await http.get("/products", { params: { vendor_id: v.data.id, status: "" } });
      setProducts(p.data);
      const o = await http.get("/orders");
      setOrders(o.data);
    } catch (err) {
      if (err.response?.status === 404) { nav("/vendor/register", { replace: true }); return; }
      console.error("Vendor dashboard load failed", err);
    }
  };

  useEffect(() => { load(); }, []);

  if (!vendor) {
    return (
      <div className="max-w-xl mx-auto p-10 text-center" data-testid="vendor-loading">
        <p className="text-sm text-[#5A6B5D]">Loading your vendor portal…</p>
      </div>
    );
  }

  const revenue = orders.reduce((s, o) => s + (o.items || []).filter(i => i.vendor_id === vendor.id).reduce((a, i) => a + i.line_total, 0), 0);
  const approvedCount = products.filter((p) => p.status === "approved").length;
  const pendingCount = products.filter((p) => p.status === "pending").length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono text-[#D97706] uppercase tracking-wider">Vendor Portal</div>
          <h1 className="font-serif text-4xl font-bold text-[#1A201C] mt-1">{vendor.business_name}</h1>
          <div className="mt-2 flex items-center gap-2 text-xs text-[#5A6B5D]">
            <StatusBadge status={vendor.status} />
            <span className="font-mono">GSTIN {vendor.gstin}</span>
          </div>
        </div>
        {vendor.status === "approved" && (
          <button onClick={() => setShowAdd(true)} className="btn-forest inline-flex items-center gap-2" data-testid="add-product-button">
            <Plus className="w-4 h-4" /> Add Product
          </button>
        )}
      </div>

      {vendor.status === "pending" && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm">
          <div className="flex items-center gap-2 text-amber-900 font-semibold"><Clock className="w-4 h-4" /> Application under review</div>
          <p className="text-amber-800 mt-1">Our team is verifying your GST, FSSAI and AYUSH credentials. You'll be notified within 48 hours.</p>
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric label="Total Revenue" value={formatINR(revenue)} icon={TrendingUp} />
        <Metric label="Approved SKUs" value={approvedCount} icon={CheckCircle2} />
        <Metric label="Pending Review" value={pendingCount} icon={Clock} />
        <Metric label="Commission" value={`${vendor.commission_rate}%`} icon={ShieldCheck} />
      </div>

      <Tabs defaultValue="products" className="mt-10">
        <TabsList className="bg-white border border-[#E2DDD5]">
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          <div className="grid gap-3">
            {products.length === 0 ? (
              <div className="text-center py-10 text-[#5A6B5D] text-sm">No products yet. Add your first listing.</div>
            ) : products.map((p) => (
              <div key={p.id} className="bg-white border border-[#E2DDD5] rounded-2xl p-4 flex items-center gap-4" data-testid={`vendor-product-${p.id}`}>
                <img src={mediaSrc(p.image_url)} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{p.name}</div>
                  <div className="text-xs text-[#5A6B5D] font-mono">Stock: {p.stock}</div>
                </div>
                <div className="text-sm font-bold text-[#1E3A2B]">{formatINR(p.price)}</div>
                <StatusBadge status={p.status} />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="space-y-3">
            {orders.length === 0 ? (
              <div className="text-center py-10 text-[#5A6B5D] text-sm">No orders yet.</div>
            ) : orders.map((o) => (
              <div key={o.id} className="bg-white border border-[#E2DDD5] rounded-2xl p-4">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <div className="text-xs font-mono text-[#5A6B5D]">ORDER #{o.id.slice(0, 8).toUpperCase()}</div>
                    <div className="text-sm font-semibold mt-1">{o.user_email}</div>
                    <div className="text-xs text-[#5A6B5D] mt-1">AWB {o.awb} · {o.items.length} item(s)</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-[#1E3A2B]">{formatINR(o.total)}</div>
                    <StatusBadge status={o.status} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="payouts" className="mt-6">
          <div className="bg-white border border-[#E2DDD5] rounded-2xl p-6">
            <div className="text-xs font-mono uppercase text-[#5A6B5D]">Total Sales Since Onboarding</div>
            <div className="font-serif text-4xl font-bold text-[#1E3A2B] mt-1">{formatINR(vendor.total_sales || 0)}</div>
            <p className="text-xs text-[#5A6B5D] mt-2">
              Payouts are settled every 7–14 days after order delivery. Commission: {vendor.commission_rate}%.
            </p>
            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
              <PayoutRow date="Feb 2026" amount={vendor.total_sales * 0.4} />
              <PayoutRow date="Jan 2026" amount={vendor.total_sales * 0.35} />
              <PayoutRow date="Dec 2025" amount={vendor.total_sales * 0.25} />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {showAdd && <AddProductModal onClose={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function Metric({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[#E2DDD5] rounded-2xl p-4">
      <Icon className="w-4 h-4 text-[#D97706]" />
      <div className="text-xs font-mono uppercase text-[#5A6B5D] mt-2">{label}</div>
      <div className="font-serif text-2xl font-bold text-[#1A201C] mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    rejected: "bg-red-50 text-red-800 border-red-200",
    placed: "bg-sky-50 text-sky-800 border-sky-200",
    processing: "bg-amber-50 text-amber-800 border-amber-200",
    dispatched: "bg-purple-50 text-purple-800 border-purple-200",
    delivered: "bg-emerald-50 text-emerald-800 border-emerald-200",
  };
  return (
    <span className={`inline-block text-[10px] uppercase font-mono px-2 py-0.5 rounded-full border ${map[status] || "bg-gray-50 border-gray-200"}`}>
      {status}
    </span>
  );
}

function PayoutRow({ date, amount }) {
  return (
    <div className="bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-3">
      <div className="text-[10px] font-mono uppercase text-[#5A6B5D]">{date}</div>
      <div className="font-bold text-[#1E3A2B]">{formatINR(amount)}</div>
    </div>
  );
}

function AddProductModal({ onClose }) {
  const [f, setF] = useState({
    name: "", description: "", price: 0, original_price: 0, stock: 0,
    category: "herbal-supplements", dosha: "tridoshic",
    benefits: "", ingredients: "", images: [], video_url: null,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (f.images.length === 0) return toast.error("Please upload at least one product photo");
    try {
      await http.post("/products", {
        ...f,
        price: parseFloat(f.price),
        original_price: parseFloat(f.original_price) || parseFloat(f.price),
        stock: parseInt(f.stock),
        benefits: f.benefits.split(",").map((s) => s.trim()).filter(Boolean),
        ingredients: f.ingredients.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Product submitted for review");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const set = (k, v) => setF({ ...f, [k]: v });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="add-product-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={submit} className="p-6 space-y-4">
          <h2 className="font-serif text-2xl font-bold">Add Product</h2>
          <input required placeholder="Product name" value={f.name} onChange={(e) => set("name", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="prod-name" />
          <textarea required placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm min-h-[80px]" data-testid="prod-desc" />
          <div className="grid grid-cols-3 gap-3">
            <input required type="number" placeholder="Price ₹" value={f.price} onChange={(e) => set("price", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="prod-price" />
            <input type="number" placeholder="MRP ₹" value={f.original_price} onChange={(e) => set("original_price", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
            <input required type="number" placeholder="Stock" value={f.stock} onChange={(e) => set("stock", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="prod-stock" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={f.category} onChange={(e) => set("category", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm">
              <option value="herbal-supplements">Herbal Supplements</option>
              <option value="personal-care">Personal Care</option>
              <option value="immunity-boosters">Immunity Boosters</option>
              <option value="organic-teas">Organic Teas</option>
              <option value="essential-oils">Essential Oils</option>
              <option value="wellness-foods">Wellness Foods</option>
              <option value="indian-masala">Indian Sabut & Pisa Masala</option>
            </select>
            <select value={f.dosha} onChange={(e) => set("dosha", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm">
              <option value="vata">Vata</option>
              <option value="pitta">Pitta</option>
              <option value="kapha">Kapha</option>
              <option value="tridoshic">Tridoshic</option>
            </select>
          </div>
          <input placeholder="Benefits (comma-separated)" value={f.benefits} onChange={(e) => set("benefits", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
          <input placeholder="Ingredients (comma-separated)" value={f.ingredients} onChange={(e) => set("ingredients", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
          <ProductMediaUploader images={f.images} videoUrl={f.video_url} onChange={({ images, videoUrl }) => setF({ ...f, images, video_url: videoUrl })} />
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-[#E2DDD5] text-sm">Cancel</button>
            <button className="btn-forest text-sm" data-testid="submit-product">Submit for Review</button>
          </div>
        </form>
      </div>
    </div>
  );
}
